import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Theme,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { pick } from "lodash";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import {
  ISkuContentRuleOrganization,
  SkuContentRuleOrganizationRequest,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";

const useStyles = (theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 30px",
  },
  paper: {
    marginTop: 10,
  },
});

type SkuRuleOrganizationFormProps = {
  skuId: string;
  skuRuleId: string;
  skuContentRuleOrganization: ISkuContentRuleOrganization | null | undefined;
  dialogTitleKey: string;
  onClose: () => void;
  onCreate: () => void;
  onUpdate: () => void;
};

type SkuRuleOrganizationFormValues = {
  id: string | undefined;
  organizationId: string | undefined;
};

const SkuRuleDetailOrganizationForm = ({
  skuId,
  skuRuleId,
  skuContentRuleOrganization,
  dialogTitleKey,
  onClose,
  onCreate,
  onUpdate,
}: SkuRuleOrganizationFormProps) => {
  const { skuService } = useGlobalContext();
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const { organizationId } = contextStore.useState();

  const init = useMemo(
    () => ({
      skuRuleId,
      ...pick(skuContentRuleOrganization ?? {}, ["id", "organizationId"]),
    }),
    [skuContentRuleOrganization, skuRuleId]
  );

  const onSubmit = async (values: SkuRuleOrganizationFormValues) => {
    try {
      const response = await skuService.saveSkuContentRuleOrganization(
        skuId,
        skuRuleId,
        SkuContentRuleOrganizationRequest.fromJS(values)
      );

      !values.id ? onCreate() : onUpdate();

      onClose();

      return response;
    } catch (err: any) {
      console.log(err);
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({
        handleSubmit,
        values,
        submitting,
        submitError,
        form: { change },
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <DialogTitle id="form-dialog-title">
              <T id={dialogTitleKey} />
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={4}>
                <ContextOrganizationField
                  values={values}
                  formChange={change}
                  label="ownerOrganization"
                  mode={
                    organizationId
                      ? ContextOrganizationFieldMode.EditManuallyTriggered
                      : ContextOrganizationFieldMode.SetByHeader
                  }
                />

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              {submitting && <CircularProgress size={16} />}
              <Button
                disabled={submitting}
                onClick={onClose}
                data-cy="cancelButton"
              >
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                data-cy="submitButton"
              >
                {t({ id: "saveFilterLabel" })}
              </Button>
            </DialogActions>
            <SnackbarMessage
              snackbarMessage={snackbarMessage}
              setSnackbarMessage={setSnackbarMessage}
            />
          </form>
        );
      }}
    />
  );
};

export default SkuRuleDetailOrganizationForm;
